import { useState, useCallback } from 'react';
import { Bell, CheckCircle2, Smartphone, X, ChevronRight, Loader2, AlertCircle, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

interface NotificationOnboardingScreenProps {
  onComplete: () => void;
  onSkip: () => void;
  onRequestPermission: () => Promise<boolean>;
  showIOSGuide?: boolean;
}

type Step = 'intro' | 'prepare' | 'request' | 'success' | 'denied' | 'error';

export function NotificationOnboardingScreen({
  onComplete,
  onSkip,
  onRequestPermission,
  showIOSGuide = false,
}: NotificationOnboardingScreenProps) {
  const [step, setStep] = useState<Step>(showIOSGuide ? 'intro' : 'intro');
  const [isRequesting, setIsRequesting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const handleRequestPermission = useCallback(async () => {
    setIsRequesting(true);
    setStep('request');
    setErrorMessage(null);
    
    try {
      const success = await onRequestPermission();
      
      if (success) {
        setStep('success');
        setTimeout(() => {
          onComplete();
        }, 1500);
      } else {
        // Check if it's a permission denial or an error
        if (Notification.permission === 'denied') {
          setStep('denied');
        } else {
          setStep('error');
          setErrorMessage('L\'activation a échoué. Veuillez réessayer.');
        }
      }
    } catch (error) {
      console.error('[Onboarding] Permission request error:', error);
      setStep('error');
      setErrorMessage(error instanceof Error ? error.message : 'Une erreur est survenue');
    }
    setIsRequesting(false);
  }, [onRequestPermission, onComplete]);

  const handleRetry = useCallback(() => {
    setStep('prepare');
    setErrorMessage(null);
  }, []);

  // iOS PWA Installation Guide
  if (showIOSGuide) {
    return (
      <div className="fixed inset-0 z-[100] bg-background flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-end p-4 safe-top">
          <button
            onClick={onSkip}
            className="p-2 rounded-full hover:bg-muted transition-colors"
            aria-label="Fermer"
          >
            <X className="w-5 h-5 text-muted-foreground" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 flex flex-col items-center justify-center px-6 pb-safe-bottom">
          <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center mb-6 animate-fade-in">
            <Smartphone className="w-10 h-10 text-primary" />
          </div>
          
          <h1 className="text-2xl font-semibold text-foreground text-center mb-3 animate-fade-in" style={{ animationDelay: '100ms' }}>
            Installez l'application
          </h1>
          
          <p className="text-muted-foreground text-center mb-8 max-w-xs animate-fade-in" style={{ animationDelay: '200ms' }}>
            Pour recevoir les notifications sur iPhone, installez d'abord l'app sur votre écran d'accueil.
          </p>

          <div className="w-full max-w-xs space-y-4 animate-fade-in" style={{ animationDelay: '300ms' }}>
            <div className="flex items-start gap-4 p-4 bg-muted/50 rounded-xl">
              <div className="w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-semibold shrink-0">1</div>
              <div>
                <p className="font-medium text-foreground">Appuyez sur Partager</p>
                <p className="text-sm text-muted-foreground">Icône 📤 en bas de Safari</p>
              </div>
            </div>
            
            <div className="flex items-start gap-4 p-4 bg-muted/50 rounded-xl">
              <div className="w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-semibold shrink-0">2</div>
              <div>
                <p className="font-medium text-foreground">Sur l'écran d'accueil</p>
                <p className="text-sm text-muted-foreground">Faites défiler et sélectionnez</p>
              </div>
            </div>
            
            <div className="flex items-start gap-4 p-4 bg-muted/50 rounded-xl">
              <div className="w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-semibold shrink-0">3</div>
              <div>
                <p className="font-medium text-foreground">Ouvrez l'app</p>
                <p className="text-sm text-muted-foreground">Depuis votre écran d'accueil</p>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 safe-bottom">
          <Button onClick={onSkip} className="w-full" size="lg">
            J'ai compris
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-[100] bg-background flex flex-col">
      {/* Header with skip button */}
      <div className="flex items-center justify-end p-4 safe-top">
        {step !== 'success' && step !== 'request' && (
          <button
            onClick={onSkip}
            className="text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            Plus tard
          </button>
        )}
      </div>

      {/* Content */}
      <div className="flex-1 flex flex-col items-center justify-center px-6">
        {/* Step: Intro */}
        {step === 'intro' && (
          <div className="flex flex-col items-center animate-fade-in">
            <div className="w-24 h-24 rounded-full bg-primary/10 flex items-center justify-center mb-8">
              <Bell className="w-12 h-12 text-primary animate-pulse" />
            </div>
            
            <h1 className="text-2xl font-semibold text-foreground text-center mb-3">
              Ne manquez aucune réponse
            </h1>
            
            <p className="text-muted-foreground text-center mb-8 max-w-xs">
              Recevez une alerte instantanée dès qu'un concierge vous répond.
            </p>

            <div className="w-full max-w-xs space-y-3 mb-8">
              <div className="flex items-center gap-3 text-foreground">
                <CheckCircle2 className="w-5 h-5 text-primary shrink-0" />
                <span className="text-sm">Réponses en temps réel</span>
              </div>
              <div className="flex items-center gap-3 text-foreground">
                <CheckCircle2 className="w-5 h-5 text-primary shrink-0" />
                <span className="text-sm">Mises à jour sur vos demandes</span>
              </div>
              <div className="flex items-center gap-3 text-foreground">
                <CheckCircle2 className="w-5 h-5 text-primary shrink-0" />
                <span className="text-sm">Désactivable à tout moment</span>
              </div>
            </div>
          </div>
        )}

        {/* Step: Prepare */}
        {step === 'prepare' && (
          <div className="flex flex-col items-center animate-fade-in">
            <div className="w-full max-w-xs mb-8">
              {/* Mock browser permission popup */}
              <div className="bg-card border border-border rounded-2xl shadow-lg p-4 relative">
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary text-primary-foreground text-xs px-3 py-1 rounded-full font-medium">
                  Popup navigateur
                </div>
                
                <div className="flex items-center gap-3 mb-4 pt-2">
                  <div className="w-10 h-10 bg-muted rounded-lg flex items-center justify-center">
                    <Bell className="w-5 h-5 text-muted-foreground" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-foreground">conciergerie.ae</p>
                    <p className="text-xs text-muted-foreground">souhaite vous envoyer des notifications</p>
                  </div>
                </div>
                
                <div className="flex gap-2">
                  <div className="flex-1 py-2 px-4 rounded-lg bg-muted text-center text-sm text-muted-foreground">
                    Bloquer
                  </div>
                  <div className="flex-1 py-2 px-4 rounded-lg bg-primary text-center text-sm text-primary-foreground font-medium relative">
                    Autoriser
                    {/* Animated arrow pointing to Autoriser */}
                    <div className="absolute -bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
                      <div className="w-0 h-0 border-l-8 border-r-8 border-b-8 border-l-transparent border-r-transparent border-b-primary" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <h1 className="text-2xl font-semibold text-foreground text-center mb-3">
              Une fenêtre va s'afficher
            </h1>
            
            <p className="text-muted-foreground text-center mb-2 max-w-xs">
              Quand elle apparaît, appuyez sur
            </p>
            
            <p className="text-primary font-semibold text-lg mb-8">
              « Autoriser »
            </p>
          </div>
        )}

        {/* Step: Request (loading) */}
        {step === 'request' && (
          <div className="flex flex-col items-center animate-fade-in">
            <div className="w-24 h-24 rounded-full bg-primary/10 flex items-center justify-center mb-8">
              <Loader2 className="w-12 h-12 text-primary animate-spin" />
            </div>
            
            <h1 className="text-2xl font-semibold text-foreground text-center mb-3">
              Activation en cours...
            </h1>
            
            <p className="text-muted-foreground text-center max-w-xs">
              Cliquez sur « Autoriser » dans la fenêtre de votre navigateur
            </p>
          </div>
        )}

        {/* Step: Success */}
        {step === 'success' && (
          <div className="flex flex-col items-center animate-fade-in">
            <div className="w-24 h-24 rounded-full bg-green-500/10 flex items-center justify-center mb-8">
              <CheckCircle2 className="w-12 h-12 text-green-500" />
            </div>
            
            <h1 className="text-2xl font-semibold text-foreground text-center mb-3">
              Notifications activées !
            </h1>
            
            <p className="text-muted-foreground text-center max-w-xs">
              Vous recevrez une alerte à chaque nouvelle réponse.
            </p>
          </div>
        )}

        {/* Step: Denied */}
        {step === 'denied' && (
          <div className="flex flex-col items-center animate-fade-in">
            <div className="w-24 h-24 rounded-full bg-amber-500/10 flex items-center justify-center mb-8">
              <Bell className="w-12 h-12 text-amber-500" />
            </div>
            
            <h1 className="text-2xl font-semibold text-foreground text-center mb-3">
              Notifications non activées
            </h1>
            
            <p className="text-muted-foreground text-center mb-6 max-w-xs">
              Vous pouvez les activer plus tard depuis votre profil.
            </p>
            
            <div className="w-full max-w-xs p-4 bg-muted/50 rounded-xl">
              <p className="text-sm font-medium text-foreground mb-2">Pour activer manuellement :</p>
              <ol className="text-sm text-muted-foreground space-y-1 list-decimal list-inside">
                <li>Cliquez sur 🔒 dans la barre d'adresse</li>
                <li>Trouvez "Notifications"</li>
                <li>Changez en "Autoriser"</li>
              </ol>
            </div>
          </div>
        )}

        {/* Step: Error (activation failed but not denied) */}
        {step === 'error' && (
          <div className="flex flex-col items-center animate-fade-in">
            <div className="w-24 h-24 rounded-full bg-red-500/10 flex items-center justify-center mb-8">
              <AlertCircle className="w-12 h-12 text-red-500" />
            </div>
            
            <h1 className="text-2xl font-semibold text-foreground text-center mb-3">
              Échec de l'activation
            </h1>
            
            <p className="text-muted-foreground text-center mb-4 max-w-xs">
              {errorMessage || 'Une erreur est survenue lors de l\'activation.'}
            </p>
            
            <div className="w-full max-w-xs p-4 bg-muted/50 rounded-xl mb-4">
              <p className="text-sm font-medium text-foreground mb-2">Essayez ces solutions :</p>
              <ol className="text-sm text-muted-foreground space-y-1 list-decimal list-inside">
                <li>Rechargez la page et réessayez</li>
                <li>Vérifiez votre connexion internet</li>
                <li>Si vous êtes sur iPhone, assurez-vous que l'app est installée sur l'écran d'accueil</li>
              </ol>
            </div>
          </div>
        )}
      </div>

      {/* Footer with action buttons */}
      <div className="p-6 safe-bottom">
        {step === 'intro' && (
          <Button 
            onClick={() => setStep('prepare')} 
            className="w-full" 
            size="lg"
          >
            Continuer
            <ChevronRight className="w-5 h-5 ml-1" />
          </Button>
        )}

        {step === 'prepare' && (
          <Button 
            onClick={handleRequestPermission} 
            className="w-full" 
            size="lg"
            disabled={isRequesting}
          >
            J'ai compris, activer
            <ChevronRight className="w-5 h-5 ml-1" />
          </Button>
        )}

        {step === 'denied' && (
          <Button 
            onClick={onSkip} 
            variant="outline"
            className="w-full" 
            size="lg"
          >
            Fermer
          </Button>
        )}

        {step === 'error' && (
          <div className="space-y-3">
            <Button 
              onClick={handleRetry} 
              className="w-full" 
              size="lg"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Réessayer
            </Button>
            <Button 
              onClick={onSkip} 
              variant="outline"
              className="w-full" 
              size="lg"
            >
              Plus tard
            </Button>
          </div>
        )}
      </div>

      {/* Progress indicator */}
      {(step === 'intro' || step === 'prepare') && (
        <div className="flex justify-center gap-2 pb-6">
          <div className={cn(
            "w-2 h-2 rounded-full transition-colors",
            step === 'intro' ? "bg-primary" : "bg-muted"
          )} />
          <div className={cn(
            "w-2 h-2 rounded-full transition-colors",
            step === 'prepare' ? "bg-primary" : "bg-muted"
          )} />
        </div>
      )}
    </div>
  );
}
